// Achievable kWp as the dashboard reads it. The backend works out each figure
// (achievable_kwp.infer_kwp_options): where the grid's kWac is the only limit on a route,
// the Manager records its kWp option with no figure and the backend infers it from that
// route's kWac at the max-out design's DC/AC ratio, else the committed one, else the
// proposed one. The agents read the same figure. The table cell, the totals
// and the kWp sort all read through here, so they agree.
import type { DashboardRow, KwpOption, RatioBasis } from "../api/hooks/useDashboard";

export type { InferredKwp, KwpOption } from "../api/hooks/useDashboard";

// How a ratio's basis reads in a sentence, as achievable_kwp.BASIS_NAMES says it to agents.
export const RATIO_BASIS_NAMES: Record<RatioBasis, string> = {
  max_out: "max-out design",
  committed: "committed",
  proposed: "proposed",
};

// Undefined: no figure recorded and none to infer. A row kept from before the backend
// inferred carries no achievable_kwp, and reads its recorded options as they are.
export const achievableKwpOptions = (
  row: DashboardRow,
): KwpOption[] | undefined =>
  row.achievable_kwp === undefined
    ? row.attributes["achievable_kwp"]?.options.map((o) => ({
        value: typeof o.value === "number" ? o.value : null,
        label: o.label,
        inferred: null,
      }))
    : (row.achievable_kwp ?? undefined);

// The smaller option with a figure, as the totals and the sort read an open choice.
export const achievableKwp = (
  row: DashboardRow,
): { kwp: number; inferred: boolean } | undefined => {
  const figures = (achievableKwpOptions(row) ?? []).filter(
    (o): o is KwpOption & { value: number } => o.value !== null,
  );
  if (!figures.length) return undefined;
  const smallest = figures.reduce((a, b) => (b.value < a.value ? b : a));
  return { kwp: smallest.value, inferred: smallest.inferred !== null };
};
